package com.advaita.cloud.drive.shared

import kotlinx.serialization.EncodeDefault
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.Serializable

/**
 * `GET /api/pipeline/curated/catalog` — the OPCs a scientist can launch, read from
 * **wisp's registry** rather than declared here.
 *
 * This replaces a hardcoded `OPC_CATALOG` list, and the reason is not tidiness. A
 * hand-maintained copy of someone else's registry drifts, and it drifts *silently*:
 * on 2026-08-17 our catalog said `demo-read-qc` while the registered document's id
 * was `demo`, and the resulting qa run was accepted with a 202 and a run_id and then
 * never launched — because an unregistered `job_type` is not rejected at submit
 * (wisp's `validate_params` finds no template and passes on its cut-over
 * conditional). The file sat at Processing forever with clean logs on both sides.
 *
 * wisp's `GET /offerings` exists precisely to end that: it is a projection over the
 * SAME assembled templates its matcher uses, so — in its own words — "the catalog can
 * never disagree with what a submit will actually match". We project it once more,
 * into the shape the picker needs.
 *
 * **The registry owns the vocabulary.** [jobType] is whatever the curator registered;
 * it is not a name DataHub chooses, validates or prettifies.
 */
@OptIn(ExperimentalSerializationApi::class)
@Serializable
data class CuratedCatalogResponse(
    val workflows: List<CuratedWorkflowDto> = emptyList(),
    /**
     * WHY this list is the length it is. An empty [workflows] used to be the answer to
     * three unrelated questions — curated is switched off here, the registry could not be
     * reached, and the registry is genuinely empty — and the UI could only say the one
     * benign thing that was true of all three ("no workflows available"). So a registry
     * outage read to the scientist as a normal, quiet day.
     *
     * The status is carried in a 200 rather than a 5xx on purpose: DataHub itself is
     * healthy and answering correctly, and a 503 here would tell a health probe pointed
     * at DataHub that DataHub is down, which is false.
     */
    @EncodeDefault
    val status: CuratedCatalogStatus = CuratedCatalogStatus.AVAILABLE,
)

/** Why [CuratedCatalogResponse.workflows] is what it is. */
@Serializable
enum class CuratedCatalogStatus {
    /** The registry answered. [CuratedCatalogResponse.workflows] may still be empty. */
    AVAILABLE,

    /**
     * The registry could not be reached, or did not answer in time. Nothing is known
     * about what could be run — which is a PROBLEM to report, not an empty list to
     * render. Transient by nature, and never the scientist's to fix.
     */
    UNAVAILABLE,

    /** Curated workflows are switched off for this deployment. Not a fault. */
    DISABLED,
}

/**
 * One opinionated use-case, projected from a wisp offering.
 *
 * Deliberately NOT carrying the pipeline coordinate or pinned revision: the offering
 * projection serves `{job_type, title, description, size, parameters, file_inputs}`
 * and has neither, so a card that showed them would be reading a second source. See
 * the note at `OpcSelectView`.
 */
@Serializable
data class CuratedWorkflowDto(
    /**
     * wisp's `job_type` — the registered document's `id`, which `TemplateExpand`
     * turns into the matcher key (`when.job_type = s["id"]`). Sent back verbatim as
     * [CuratedRunRequest.jobType]; any mangling here is a run that never launches.
     */
    val jobType: String,
    val title: String,
    val description: String,
    /** The curator's t-shirt size hint (`small`/`medium`/`large`), or null. Display only. */
    val size: String? = null,
    /** The file inputs the scientist picks for, in the order the schema declares them. */
    val inputs: List<CuratedInputDto> = emptyList(),
    /**
     * The curator's scalar knobs — every schema property that is NOT a file input, in
     * declaration order. Empty for an OPC that declares none, which is why the picker
     * shipped without them.
     *
     * These were deliberately unprojected while no registered OPC had one ("rendering
     * them needs a form-control model the wizard doesn't have"). The rnaseq OUC broke
     * that: it declares `reference`, a `format: reference` pick naming a wisp reference
     * set, and wisp's `classify` expands that set's members into the run's params. With
     * the knob unprojected the scientist could not send it, nothing applies a schema
     * `default` (`hydrate` takes `params.params` verbatim), and the pick was inert — the
     * reference came from the driver image's config layer instead, so the enum on the
     * card was decoration.
     */
    val knobs: List<CuratedKnobDto> = emptyList(),
)

/**
 * One scalar knob — a schema property the scientist sets by typing or choosing rather
 * than by picking files. Values go back as strings under [CuratedRunRequest.params]
 * (that map is `Map<String, String>`); wisp routes each to the process or data layer
 * from the `layer` tag it stamped at registration, so DataHub never classifies them.
 */
@Serializable
data class CuratedKnobDto(
    /** The schema property name, and the key the value is submitted under. */
    val name: String,
    val description: String,
    /** The curator's longer `help_text`, when they wrote one. */
    val helpText: String? = null,
    /** From the schema's `required` array. */
    val required: Boolean = false,
    /**
     * The declared `default`, stringified, or null. Rendered as the initial value — NOT
     * silently sent on the scientist's behalf. Nothing in the chain applies a schema
     * default (wisp's `hydrate` passes the submitted knobs through untouched), so an
     * unrendered default reaches no run at all; making it the control's starting value
     * is what turns it into the value that actually launches.
     */
    val default: String? = null,
    /**
     * The declared `enum`, in schema order — a closed set, so the control is a select.
     * Empty means free text. A `type: boolean` property arrives here as `true`/`false`
     * for the same reason: the curator declared two legal values, and a select is the
     * honest control for two legal values.
     */
    val choices: List<String> = emptyList(),
    /**
     * The schema `format`, verbatim, when the curator declared one. `reference` marks a
     * wisp reference-set pick — one name standing for a whole bundle of members, which
     * is what makes "a genome paired with an index built from a different annotation"
     * unrepresentable. Display only here; the expansion is `classify`'s job.
     */
    val format: String? = null,
)

/**
 * One file input — a `format: file-path` or `file-set` property of the offering's
 * JSON Schema, flattened to what the picker needs.
 */
@Serializable
data class CuratedInputDto(
    /** The schema property name, and the key picks are submitted under. */
    val name: String,
    /** [CuratedInputFormatWire.FILE_PATH] or [CuratedInputFormatWire.FILE_SET]. */
    val format: String,
    /**
     * The curator's declared `path:` — the exact target for a `file-path`, the root
     * members land under for a `file-set`. May be blank if the curator omitted it,
     * which resolve tolerates (falling back to the pick's `rel`) but which leaves the
     * LAUNCH value wrong, so a blank here is worth surfacing rather than hiding.
     */
    val targetPath: String,
    val description: String,
    /** The curator's longer `help_text`, when they wrote one. */
    val helpText: String? = null,
    /** From the schema's `required` array. */
    val required: Boolean = true,
    /**
     * The schema's `pattern`, verbatim, when the curator declared one — used for the
     * picker's dimming and auto-selection. Absent means UNCONSTRAINED: every file is
     * selectable. That is a deliberate behaviour change from the hardcoded catalog,
     * which fell back to an invented `acceptedExtensions` list; inventing a constraint
     * the curator didn't write is how a picker hides a file the pipeline would accept.
     */
    val pattern: String? = null,
)
